import { supabase } from '@/lib/supabase'
import { paymentMethodsApi } from '@/api/paymentMethods'
import { subscribersApi, type SubscriberTrialExtension } from '@/api/subscribers'

export type PulseCounts = {
  contacts: number
  deals: number
  tasks: number
  activities: number
  messages: number
  documents: number
  total: number
}

export type LastOutreach = {
  channel: string
  delivered: boolean
  sender: string
  sent_at: string
  status: string
  failure_code: string | number | null
}

export type NextJourneyStep = {
  scheduled_for: string | null
  channel: string | null
  label: string | null
}

export type PulseTimelineEvent = {
  at: string
  change_type: string
  actor_kind: string
  entity_id: string | null
  source: string | null
}

export type PulseOrg = {
  organization_id: string
  name: string
  plan_code: string | null
  plan_name: string | null
  subscription_state: string
  price_agorot: number
  trial_days_left: number | null
  trial_no_end_date: boolean
  last_login_at: string | null
  owner_name: string | null
  owner_phone: string | null
  human: PulseCounts
  system: PulseCounts
  severity: string
  handler: string
  last_outreach: LastOutreach | null
  next_journey_step: NextJourneyStep | null
  timeline: PulseTimelineEvent[]
}

export type OrgPulseSnapshot = {
  as_of: string
  window_start: string
  window_end: string
  window_end_inclusive: boolean
  orgs: PulseOrg[]
}

export type TrialDealStage = 'trial' | 'follow_up' | 'lost' | 'won' | 'unknown'

export type TrialManagementOrg = PulseOrg & {
  trial_end: string | null
  trial_days_left: number | null
  deal_stage: TrialDealStage
  deal_stage_label: string
  has_saved_card: boolean
  latest_extension: SubscriberTrialExtension | null
}

const DEAL_STAGE_LABEL: Record<TrialDealStage, string> = {
  trial: 'התנסות',
  follow_up: 'התנסות הסתיימה — מעקב',
  lost: 'לא נסגר',
  won: 'נסגר',
  unknown: '—',
}

export class OrgPulseForbiddenError extends Error {
  readonly code = '42501'
  constructor() {
    super('org_pulse_forbidden')
    this.name = 'OrgPulseForbiddenError'
  }
}

const EMPTY_COUNTS: PulseCounts = {
  contacts: 0,
  deals: 0,
  tasks: 0,
  activities: 0,
  messages: 0,
  documents: 0,
  total: 0,
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function num(value: unknown): number {
  const n = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(n) ? n : 0
}

function str(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null
}

function counts(value: unknown): PulseCounts {
  if (!isRecord(value)) return { ...EMPTY_COUNTS }
  return {
    contacts: num(value.contacts),
    deals: num(value.deals),
    tasks: num(value.tasks),
    activities: num(value.activities),
    messages: num(value.messages),
    documents: num(value.documents),
    total: num(value.total),
  }
}

function outreach(value: unknown): LastOutreach | null {
  if (!isRecord(value)) return null
  const sentAt = str(value.sent_at)
  if (!sentAt) return null
  const failure = value.failure_code
  return {
    channel: str(value.channel) ?? '',
    delivered: value.delivered === true,
    sender: str(value.sender) ?? '',
    sent_at: sentAt,
    status: str(value.status) ?? '',
    failure_code: typeof failure === 'string' || typeof failure === 'number' ? failure : null,
  }
}

function nextStep(value: unknown): NextJourneyStep | null {
  if (!isRecord(value)) return null
  return {
    scheduled_for: str(value.scheduled_for),
    channel: str(value.channel),
    label: str(value.label),
  }
}

function timeline(value: unknown): PulseTimelineEvent[] {
  if (!Array.isArray(value)) return []
  return value.flatMap((item) => {
    if (!isRecord(item) || typeof item.at !== 'string') return []
    return [{
      at: item.at,
      change_type: str(item.change_type) ?? '',
      actor_kind: str(item.actor_kind) ?? '',
      entity_id: str(item.entity_id),
      source: str(item.source),
    }]
  })
}

function org(value: unknown): PulseOrg | null {
  if (!isRecord(value) || typeof value.organization_id !== 'string') return null
  return {
    organization_id: value.organization_id,
    name: str(value.name) ?? 'ללא שם',
    plan_code: str(value.plan_code),
    plan_name: str(value.plan_name),
    subscription_state: str(value.subscription_state) ?? 'expired',
    price_agorot: num(value.price_agorot),
    trial_days_left: value.trial_days_left == null ? null : num(value.trial_days_left),
    trial_no_end_date: value.trial_no_end_date === true,
    last_login_at: str(value.last_login_at),
    owner_name: str(value.owner_name),
    owner_phone: str(value.owner_phone),
    human: counts(value.human),
    system: counts(value.system),
    severity: str(value.severity) ?? 'grey',
    handler: str(value.handler) ?? 'nobody',
    last_outreach: outreach(value.last_outreach),
    next_journey_step: nextStep(value.next_journey_step),
    timeline: timeline(value.timeline),
  }
}

export function parseOrgPulseSnapshot(data: unknown): OrgPulseSnapshot {
  if (!isRecord(data)) {
    throw new Error('org_pulse_snapshot returned an empty payload')
  }
  const orgs = Array.isArray(data.orgs)
    ? data.orgs.flatMap((row) => {
        const parsed = org(row)
        return parsed ? [parsed] : []
      })
    : []
  return {
    as_of: str(data.as_of) ?? new Date().toISOString(),
    window_start: str(data.window_start) ?? new Date().toISOString(),
    window_end: str(data.window_end) ?? new Date().toISOString(),
    window_end_inclusive: data.window_end_inclusive === true,
    orgs,
  }
}

function forbidden(error: { code?: string; message?: string }): boolean {
  const code = error.code ?? ''
  const message = error.message ?? ''
  return (
    code === '42501'
    || code === 'PGRST301'
    || /42501|org_pulse_forbidden|permission denied|not authorized/i.test(message)
  )
}

export const orgPulseApi = {
  async snapshot(includeTest = false): Promise<OrgPulseSnapshot> {
    const { data, error } = await supabase.rpc('org_pulse_snapshot', {
      p_include_test: includeTest,
    })
    if (error) {
      if (forbidden(error)) throw new OrgPulseForbiddenError()
      throw error
    }
    return parseOrgPulseSnapshot(data)
  },

  async trialManagement(includeTest = false): Promise<TrialManagementOrg[]> {
    const snapshot = await this.snapshot(includeTest)
    const ids = snapshot.orgs.map((item) => item.organization_id)
    if (ids.length === 0) return []

    const [{ data: subscriptions, error: subscriptionError }, { data: dealRows, error: dealError }] = await Promise.all([
      supabase
        .from('org_system_subscriptions')
        .select('organization_id, status, expires_at, created_at')
        .eq('system_code', 'jumpstart')
        .in('organization_id', ids),
      supabase
        .from('crm_deals')
        .select('stage_code, metadata, updated_at')
        .eq('organization_id', 'c3aa7a0d-461a-4ed4-882a-58bd063b1e62')
        .eq('source_type', 'trial_provision'),
    ])
    if (subscriptionError) throw subscriptionError
    if (dealError) throw dealError

    const subsByOrg = new Map<string, Array<{ status: string; expires_at: string | null; created_at: string }>>()
    for (const row of subscriptions ?? []) {
      const rows = subsByOrg.get(row.organization_id) ?? []
      rows.push(row)
      subsByOrg.set(row.organization_id, rows)
    }

    const dealByOrg = new Map<string, { stage_code: string; updated_at: string }>()
    for (const row of dealRows ?? []) {
      const metadata = row.metadata as Record<string, unknown> | null
      const orgId = typeof metadata?.trial_org_id === 'string' ? metadata.trial_org_id : null
      if (!orgId) continue
      const current = dealByOrg.get(orgId)
      if (!current || row.updated_at > current.updated_at) {
        dealByOrg.set(orgId, { stage_code: row.stage_code, updated_at: row.updated_at })
      }
    }

    const now = Date.now()
    const cutoff = now - 30 * 24 * 60 * 60 * 1000
    const candidates = snapshot.orgs.flatMap((item) => {
      const rows = [...(subsByOrg.get(item.organization_id) ?? [])]
        .sort((a, b) => b.created_at.localeCompare(a.created_at))
      if (rows.some((row) => ['active', 'paid'].includes(row.status))) return []
      const trial = rows.find((row) => row.status === 'trial' || row.status === 'trialing')
      const recentlyEnded = rows.find((row) => {
        if (!row.expires_at) return false
        const end = new Date(row.expires_at).getTime()
        return Number.isFinite(end) && end >= cutoff && end < now
      })
      const current = trial ?? recentlyEnded
      if (!current) return []
      return [{ item, current }]
    })

    const enriched = await mapLimited(candidates, 5, async ({ item, current }) => {
      const [card, history] = await Promise.all([
        paymentMethodsApi.status(item.organization_id).catch(() => null),
        subscribersApi.trialExtensionHistory(item.organization_id).catch(() => []),
      ])
      const endMs = current.expires_at ? new Date(current.expires_at).getTime() : NaN
      const deal = dealByOrg.get(item.organization_id)
      const dealStage = normalizeDealStage(deal?.stage_code)
      return {
        ...item,
        subscription_state: current.status,
        trial_end: current.expires_at,
        trial_days_left: Number.isFinite(endMs) ? Math.ceil((endMs - now) / (24 * 60 * 60 * 1000)) : null,
        trial_no_end_date: !current.expires_at,
        deal_stage: dealStage,
        deal_stage_label: DEAL_STAGE_LABEL[dealStage],
        has_saved_card: card?.has_payment_method === true,
        latest_extension: history[0] ?? null,
      }
    })

    return enriched.sort((a, b) => {
      if (!a.trial_end) return 1
      if (!b.trial_end) return -1
      return a.trial_end.localeCompare(b.trial_end)
    })
  },
}

function normalizeDealStage(stage: string | undefined): TrialDealStage {
  if (!stage) return 'unknown'
  if (stage === 'trial') return 'trial'
  if (stage === 'qualified') return 'follow_up'
  if (stage === 'lost') return 'lost'
  if (['won', 'negotiation', 'in_progress', 'completed'].includes(stage)) return 'won'
  return 'unknown'
}

async function mapLimited<T, R>(items: T[], size: number, mapper: (item: T) => Promise<R>): Promise<R[]> {
  const result: R[] = []
  for (let index = 0; index < items.length; index += size) {
    result.push(...await Promise.all(items.slice(index, index + size).map(mapper)))
  }
  return result
}
